<template>
  <div class="workbench-main" :class="{ readonly: readonly }">
    <!-- 服务选择 -->
    <div class="service-cards">
      <button
          v-for="item in services"
          :key="item.key"
          type="button"
          class="service-card"
          :class="{ active: activeService === item.key }"
          @click="switchService(item.key)">
        <div class="card-head">
          <img class="card-icon" :src="item.iconSrc" :alt="item.label" />
          <span class="card-title">{{ item.label }}</span>
        </div>
        <span class="card-desc">{{ item.desc }}</span>
        <i v-if="activeService === item.key" class="bi bi-check-circle-fill check-icon"></i>
      </button>
    </div>

    <!-- 功能工作区 -->
    <div class="feature-workspace">
      <div class="options-card">
        <div class="rewrite-options-wrapper">
          <div class="rewrite-options-bar">
            <div class="rewrite-options-row">
              <div class="rewrite-options-language">
                <span class="options-label">语言：</span>
                <div class="options-pills">
                  <button
                      v-for="lang in languages"
                      :key="lang"
                      type="button"
                      class="pill-btn pill-lang"
                      :class="{ active: activeLanguage === lang }"
                      @click="switchLanguage(lang)">{{ lang }}</button>
                </div>
              </div>
              <div v-if="needsPlatform" class="rewrite-options-platform">
                <div class="platform-row">
                  <span class="options-label" :class="{ 'label-warn': !platformSelected && !readonly }">平台：</span>
                  <div
                      class="platform-pills-wrap"
                      :class="{ 'platform-unselected-flash': !platformSelected && !readonly }">
                    <span v-if="!platformSelected && !readonly" class="platform-hint">请先选择平台</span>
                    <div class="options-pills platform-pills">
                      <button
                          v-if="activeLanguage === '英文'"
                          type="button"
                          class="platform-card platform-card-turnitin"
                          :class="{ active: activePlatform === 'Turnitin' }"
                          @click="switchPlatform('Turnitin')">
                        <span class="platform-card-title">Turnitin</span>
                        <span class="platform-card-note">适配26.16版本</span>
                      </button>
                      <button
                          v-for="p in platformPills"
                          :key="p"
                          type="button"
                          class="pill-btn pill-platform"
                          :class="{ active: activePlatform === p }"
                          @click="switchPlatform(p)">{{ p }}</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="options-divider"></div>
        </div>

        <div class="feature-body">
          <div class="feature-header">
            <div class="feature-title-area">
              <h2 class="feature-title">{{ activeServiceLabel }}</h2>
              <div v-if="officialLink" class="official-link-badge">
                <a :href="officialLink.url" target="_blank" rel="noreferrer">{{ officialLink.text }}</a>
              </div>
            </div>
            <button type="button" class="history-trigger" @click="scrollToRecords">
              <i class="bi bi-clock-history"></i>
              历史记录
            </button>
          </div>

          <div class="input-tabs">
            <button
                type="button"
                class="tab-btn"
                :class="{ active: inputMode === 'text' }"
                @click="switchInputMode('text')">粘贴文本</button>
            <button
                type="button"
                class="tab-btn"
                :class="{ active: inputMode === 'file' }"
                @click="switchInputMode('file')">上传文件</button>
          </div>

          <div v-if="inputMode === 'text' && activeService === 'AIGC'" class="tips-banner">
            <p>1. 段落模式请尽量对照同平台官方检测报告，便于<strong class="text-red">独创学术模型</strong>精准优化。</p>
            <p class="tips-bold">2. 推荐上传 docx 并附带官方检测报告，由独创学术模型处理，效果更佳</p>
          </div>

          <template v-if="inputMode === 'text'">
            <div class="editor-grid">
              <div class="editor-col">
                <div class="rewrite-container">
                  <div class="rewrite-input-wrap">
                <textarea
                    v-model="content"
                    class="rewrite-input"
                    :readonly="readonly || processing"
                    :maxlength="textMaxChars"
                    placeholder="输入或粘贴一段。一次粘贴多段，或者一次只粘贴一句，均效果不佳。一次300字左右最佳。不得少于20个字符"></textarea>
                    <div class="char-counter">{{ charCount }}/{{ textMaxChars }}</div>
                  </div>
                  <div class="editor-actions">
                    <button type="button" class="btn-outline" :disabled="readonly || processing" @click="clearContent">重置</button>
                    <button type="button" class="btn-primary" :disabled="!canSubmit" @click="handleSubmit">
                      {{ processing ? '生成中…' : '一键生成' }}
                    </button>
                    <span class="cost-hint">预计消耗{{ estimatedCost }}通用点数</span>
                  </div>
                  <p v-if="needsPlatform && !platformSelected && !readonly && charCount > 0" class="platform-warn">提交前请先选择检测平台</p>
                  <p v-if="balanceInsufficient && charCount > 0" class="balance-warn">可用点数不足，请先充值</p>
                </div>
              </div>

              <div class="editor-col result-col">
                <div class="result-box">
                  <div class="result-content">
                    <div v-if="processing" class="result-processing">
                      <div class="loading-spinner"></div>
                      <p>正在处理，请稍候…</p>
                    </div>
                    <TextDiffView
                        v-else-if="showDiff && resultContent && content.trim()"
                        :original="content.trim()"
                        :modified="resultContent" />
                    <p v-else-if="resultContent" class="result-text">{{ resultContent }}</p>
                    <p v-else class="result-placeholder">生成结果将显示在这里</p>
                  </div>
                  <div class="result-switch-bar">
                    <button
                        type="button"
                        class="diff-toggle"
                        :disabled="!resultContent || !content.trim()"
                        @click="showDiff = !showDiff">
                      <i class="bi bi-arrow-left-right"></i>
                      <span>{{ showDiff ? '查看改后文字' : '查看修改文字' }}</span>
                    </button>
                  </div>
                </div>
                <button type="button" class="btn-copy" :disabled="!resultContent" @click="copyText(resultContent)">一键复制</button>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="file-processing-card">
              <div class="file-processing-header">
                <h3 class="file-processing-title">文件处理</h3>
                <div class="file-processing-title-row">
                  <div class="file-processing-intro">
                    <h4 class="file-processing-subtitle">{{ fileProcessingLabel }}</h4>
                    <p class="file-processing-subline">{{ fileProcessingSubline }}</p>
                  </div>
                </div>

                <div v-if="uploadSession" class="uploaded-files-container">
                  <div class="uploaded-file-card main-file-card">
                    <div class="uploaded-file-inner">
                      <div class="uploaded-file-info">
                        <i class="bi bi-file-earmark-word uploaded-file-icon"></i>
                        <div class="uploaded-file-meta">
                          <div class="uploaded-file-name">{{ uploadSession.sourceFileName }}</div>
                          <div class="uploaded-file-size">{{ uploadSession.fileSizeLabel }}</div>
                        </div>
                      </div>
                      <button type="button" class="uploaded-file-remove-btn" title="移除文件" :disabled="mainUploading" @click="clearMainFile">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>

                  <div v-if="uploadSession.dedupReportFileName" class="uploaded-file-card report-file-card">
                    <div class="uploaded-file-inner">
                      <div class="uploaded-file-info">
                        <i class="bi bi-file-earmark-pdf uploaded-file-icon"></i>
                        <div class="uploaded-file-meta">
                          <div class="uploaded-file-name">查重报告 · {{ uploadSession.dedupReportFileName }}</div>
                          <div class="uploaded-file-size">{{ uploadSession.dedupReportFileSizeLabel }}</div>
                        </div>
                      </div>
                      <button type="button" class="uploaded-file-remove-btn" title="移除查重报告" :disabled="reportUploading" @click="clearDedupReportFile">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>

                  <div v-if="uploadSession.reportFileName" class="uploaded-file-card report-file-card">
                    <div class="uploaded-file-inner">
                      <div class="uploaded-file-info">
                        <i :class="reportFileIconClass" class="uploaded-file-icon"></i>
                        <div class="uploaded-file-meta">
                          <div class="uploaded-file-name">{{ aigcReportCardTitle }} · {{ uploadSession.reportFileName }}</div>
                          <div class="uploaded-file-size">{{ uploadSession.reportFileSizeLabel }}</div>
                        </div>
                      </div>
                      <button type="button" class="uploaded-file-remove-btn" title="移除报告" :disabled="reportUploading" @click="clearAigcReportFile">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="file-upload-stack">
                <div v-if="!uploadSession" class="main-upload-row">
                  <div
                      class="main-file-upload"
                      :class="{ 'drag-over': mainDragOver, uploading: mainUploading }"
                      @click="triggerMainFileInput"
                      @dragover.prevent="onMainDragOver"
                      @dragleave.prevent="onMainDragLeave"
                      @drop.prevent="onMainDrop">
                    <div v-if="mainUploading" class="upload-loading-inline">
                      <div class="loading-spinner"></div>
                      <p>正在上传并解析文档…</p>
                    </div>
                    <div v-else class="main-upload-placeholder">
                      <div class="main-upload-trigger">
                        <i class="bi bi-file-earmark-plus"></i>
                        <span class="upload-text-muted">将原文件拖到此处，或</span>
                        <span class="upload-text-link">点击上传</span>
                      </div>
                      <div class="main-upload-tips">
                        <p>仅支持DOCX格式的文件，<strong>DOC文件请先在Word中转换为DOCX（"另存为"-"Word文档(.docx)"），直接修改后缀名无效</strong>。</p>
                        <p>不支持打开修订模式、批注模式等，请先接受或拒绝所有修订，并关闭所有批注，否则这些部分不会被处理</p>
                      </div>
                    </div>
                  </div>
                  <input
                      ref="mainFileInput"
                      type="file"
                      class="hidden-file-input"
                      accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      @change="onMainFileChange">
                </div>

                <div
                    v-if="needsPlatform"
                    class="report-upload-tip"
                    :class="{
                      disabled: !reportUploadEnabled,
                      'drag-over': reportDragOver && reportUploadEnabled,
                      uploading: reportUploading
                    }"
                    @dragover.prevent="onReportDragOver"
                    @dragleave.prevent="onReportDragLeave"
                    @drop.prevent="onReportDrop">
                  <div class="report-upload-content">
                    <template v-if="dualSplitReportUpload">
                      <div class="report-upload-dual-actions">
                        <button
                            type="button"
                            class="report-upload-trigger"
                            :disabled="!reportUploadEnabled || reportUploading"
                            @click="triggerDedupReportInput">
                          <span :class="reportUploadEnabled ? 'upload-text-link' : 'upload-text-muted'">上传查重报告</span>
                          <span class="upload-text-muted">（选填）</span>
                        </button>
                        <button
                            type="button"
                            class="report-upload-trigger"
                            :disabled="!reportUploadEnabled || reportUploading"
                            @click="triggerAigcReportInput">
                          <span :class="reportUploadEnabled ? 'upload-text-link' : 'upload-text-muted'">上传AIGC检测报告</span>
                          <span class="upload-text-muted">（选填）</span>
                        </button>
                      </div>
                    </template>
                    <button
                        v-else
                        type="button"
                        class="report-upload-trigger"
                        :disabled="!reportUploadEnabled || reportUploading"
                        @click="triggerReportFileInput">
                      <span :class="reportUploadEnabled ? 'upload-text-dark' : 'upload-text-muted'">上传原文后，可将报告拖到此处，或</span>
                      <span :class="reportUploadEnabled ? 'upload-text-link' : 'upload-text-muted'">点击上传</span>
                      <span :class="reportUploadEnabled ? 'upload-text-dark' : 'upload-text-muted'">（选填）</span>
                    </button>
                    <div class="report-upload-detail">
                      <div class="report-preview-thumb">
                        <img :src="reportPreviewImage" alt="检测报告示例">
                      </div>
                      <p class="report-upload-desc" v-html="reportUploadDesc"></p>
                    </div>
                  </div>
                  <input
                      ref="reportFileInput"
                      type="file"
                      class="hidden-file-input"
                      :accept="reportAcceptTypes"
                      @change="onReportFileChange">
                  <input
                      v-if="dualSplitReportUpload"
                      ref="reportDedupInput"
                      type="file"
                      class="hidden-file-input"
                      :accept="reportAcceptTypes"
                      @change="onDedupReportFileChange">
                  <input
                      v-if="dualSplitReportUpload"
                      ref="reportAigcInput"
                      type="file"
                      class="hidden-file-input"
                      :accept="reportAcceptTypes"
                      @change="onAigcReportFileChange">
                </div>

                <div v-if="uploadSession" class="file-submit-row">
                  <button type="button" class="btn-primary file-submit-btn" :disabled="!canSubmitFile" @click="handleFileSubmit">
                    {{ fileProcessingLabel }}
                  </button>
                  <span class="file-cost-hint">
                    预计消耗{{ fileEstimatedCost }}点数
                    <template v-if="fileCostDetail">（{{ fileCostDetail }}）</template>
                  </span>
                </div>
              </div>
            </div>
          </template>

          <div v-if="errorMessage" class="error-panel">
            <i class="bi bi-exclamation-circle"></i>
            <span>{{ errorMessage }}</span>
          </div>

          <!-- 文本记录 -->
          <div v-if="inputMode === 'text'" id="task-records" class="records-section">
            <div class="records-divider"></div>
            <div class="records-head">
              <h3>{{ activeServiceLabel }}文本记录</h3>
              <button type="button" class="refresh-btn" title="刷新" :disabled="recordsLoading" @click="loadRecords(true)">
                <i class="bi bi-arrow-clockwise" :class="{ spinning: recordsLoading }"></i>
              </button>
            </div>

            <div v-if="!readonly && records.length" class="records-list">
              <div
                  v-for="item in records"
                  :key="item.taskNo"
                  class="record-card">
                <div class="record-top">
                  <div class="record-meta">
                    <span>{{ formatRelativeTime(item.finishedAt || item.createTime) }}</span>
                    <span v-if="recordTag(item)" class="record-tag">{{ recordTag(item) }}</span>
                  </div>
                  <div class="record-actions">
                    <button type="button" class="btn-copy-sm" @click="copyText(item.outputContent)">一键复制</button>
                    <button
                        type="button"
                        class="diff-toggle-sm"
                        :disabled="!item.sourceContent || !item.outputContent"
                        @click="toggleRecordDiff(item.taskNo)">
                      <i class="bi bi-arrow-left-right"></i>
                      <span>{{ recordDiffMode[item.taskNo] ? '查看改后文字' : '查看修改文字' }}</span>
                    </button>
                  </div>
                </div>
                <TextDiffView
                    v-if="recordDiffMode[item.taskNo]"
                    :original="item.sourceContent"
                    :modified="item.outputContent"
                    class="record-diff-view" />
                <div v-else class="record-body">
                  <div class="record-col">
                    <div class="record-col-label">原文</div>
                    <p class="clamped">{{ item.sourceContent }}</p>
                  </div>
                  <div class="record-col">
                    <div class="record-col-label">生成结果</div>
                    <p class="clamped">{{ item.outputContent }}</p>
                  </div>
                </div>
              </div>
              <button v-if="recordsHasMore" type="button" class="load-more-btn" :disabled="recordsLoading" @click="loadMoreRecords">
                加载更多
              </button>
            </div>

            <div v-else-if="!readonly && recordsLoading" class="records-empty">
              <i class="bi bi-arrow-repeat spinning"></i>
              <p>加载中…</p>
            </div>
            <div v-else-if="readonly" class="records-empty">
              <i class="bi bi-person-lock"></i>
              <p>请先登录后查看历史记录</p>
            </div>
            <div v-else class="records-empty">
              <i class="bi bi-inbox"></i>
              <p>暂无文本记录，完成一次处理后将显示在这里</p>
            </div>
          </div>

          <!-- 文档记录 -->
          <div v-else id="document-records" class="records-section document-records-section">
            <div class="records-divider"></div>
            <div class="records-head">
              <h3>文档记录</h3>
              <button type="button" class="refresh-btn" title="刷新" :disabled="docRecordsLoading" @click="loadDocRecords(true)">
                <i class="bi bi-arrow-clockwise" :class="{ spinning: docRecordsLoading }"></i>
              </button>
            </div>

            <div class="document-records-scroll">
              <div v-if="!readonly && docRecords.length" class="records-list document-records-list">
                <div
                    v-for="item in docRecords"
                    :key="item.taskNo"
                    class="document-record-card"
                    @click="openDocRecord(item)">
                  <div class="document-record-main">
                    <i class="bi bi-file-earmark-word document-record-icon"></i>
                    <div class="document-record-info">
                      <div class="document-record-name">{{ item.sourceFileName || item.title || '未命名文档' }}</div>
                      <div class="document-record-meta">
                        <span class="document-record-status">{{ docRecordStatusLine(item) }}</span>
                        <span class="document-record-time">大约 {{ formatRelativeTime(item.createTime) }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="document-record-actions" @click.stop>
                    <button
                        type="button"
                        class="document-record-action-btn document-record-download-btn"
                        :class="{ 'is-ready': canDownloadDocRecord(item) }"
                        title="下载文件"
                        :disabled="!canDownloadDocRecord(item)"
                        @click="downloadDocRecord(item)">
                      <i class="bi bi-download"></i>
                    </button>
                    <button
                        type="button"
                        class="document-record-action-btn document-record-delete-btn"
                        title="删除记录"
                        @click="deleteDocRecord(item)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
                <button v-if="docRecordsHasMore" type="button" class="load-more-btn" :disabled="docRecordsLoading" @click="loadMoreDocRecords">
                  加载更多
                </button>
              </div>

              <div v-else-if="!readonly && docRecordsLoading" class="records-empty">
                <i class="bi bi-arrow-repeat spinning"></i>
                <p>加载中…</p>
              </div>
              <div v-else-if="readonly" class="records-empty">
                <i class="bi bi-person-lock"></i>
                <p>请先登录后查看历史记录</p>
              </div>
              <div v-else class="records-empty">
                <i class="bi bi-inbox"></i>
                <p>暂无文档记录</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <transition name="toast-fade">
      <div v-if="toastMessage" class="workbench-toast">
        <i class="bi bi-check-circle-fill"></i>
        <span>{{ toastMessage }}</span>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="pendingDeleteRecord" class="confirm-overlay" @click.self="cancelDeleteRecord">
        <div class="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="delete-record-title">
          <h3 id="delete-record-title" class="confirm-title">删除记录</h3>
          <p class="confirm-text">
            确定删除「{{ pendingDeleteRecord.sourceFileName || pendingDeleteRecord.title || '该文档' }}」吗？删除后不可恢复。
          </p>
          <div class="confirm-actions">
            <button type="button" class="btn-outline" :disabled="deletingRecord" @click="cancelDeleteRecord">取消</button>
            <button type="button" class="btn-danger" :disabled="deletingRecord" @click="confirmDeleteRecord">
              {{ deletingRecord ? '删除中…' : '删除' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { submitProcess, getProcessTask, getProcessTasks, submitProcessFile, deleteProcessRecord, uploadProcessDocx, uploadProcessReport, deleteProcessUpload, deleteProcessReport } from '@/api/portal/process'
import TextDiffView from '@/components/workbench/TextDiffView.vue'
import { loadWorkbenchPrefs, saveWorkbenchPrefs } from '@/utils/workbenchPrefs'

import jcSvg from '@/assets/svg/jc-svg.svg'
import aigcSvg from '@/assets/svg/aigc-svg.svg'
import sjSvg from '@/assets/svg/sj-svg.svg'
import xsrsSvg from '@/assets/svg/xsrs-svg.svg'

const MAX_CHARS_ZH = 1500
const MAX_CHARS_EN = 6000
const MIN_CHARS = 20
const POLL_INTERVAL = 2000
const PAGE_SIZE = 5
const DOC_RECORD_POLL_INTERVAL = 5000

const OFFICIAL_LINKS = {
  Turnitin: { url: 'https://www.turnitin.com/', text: 'Turnitin 官方入口' },
  知网: { url: 'https://cx.cnki.net', text: '知网官方查AI入口' },
  维普: { url: 'https://vpcs.fanyu.com/', text: '维普官方查AI入口' },
  格子达: { url: 'https://www.gezida.com/', text: '格子达官方入口' },
  大雅: { url: 'https://www.dayainfo.com/', text: '大雅官方入口' },
  万方: { url: 'https://check.wanfangdata.com.cn/', text: '万方官方入口' }
}

const REPORT_PLATFORM_CONFIG = {
  Turnitin: {
    accept: '.pdf,application/pdf',
    preview: 'https://img.kuaipaper.com/reportsuoluetu-zhiwang.png',
    desc: '选填上传 Turnitin 官方<strong class="text-red">检测报告</strong>（PDF），辅助<strong class="text-red">独创学术模型</strong>精准优化'
  },
  知网: {
    accept: '.pdf,application/pdf',
    preview: 'https://img.kuaipaper.com/reportsuoluetu-zhiwang.png',
    descAigc: '选填上传知网官方 <strong class="text-red">AIGC 检测报告</strong>，由<strong class="text-red">独创学术模型</strong>精准优化，表达更自然',
    descDedup: '选填上传知网官方 <strong class="text-red">查重报告</strong>，由<strong class="text-red">独创学术模型</strong>优化重复表述',
    descDual: '双降可分别上传查重与AIGC检测报告（选填），<strong class="text-red">独创学术模型</strong>协同处理；不上传亦全文优化'
  },
  维普: {
    accept: '.html,.htm,text/html',
    preview: 'https://img.kuaipaper.com/reportsuoluetu-weipu.png',
    descAigc: '选填上传维普官方 <strong class="text-red">AIGC 检测报告</strong>（HTML），由<strong class="text-red">独创学术模型</strong>精准优化',
    descDedup: '选填上传维普官方 <strong class="text-red">查重报告</strong>（HTML），由<strong class="text-red">独创学术模型</strong>优化重复表述',
    descDual: '双降可分别上传查重与AIGC检测报告 HTML（选填），<strong class="text-red">独创学术模型</strong>协同处理；不上传亦全文优化'
  },
  格子达: {
    accept: '.pdf,application/pdf',
    preview: 'https://img.kuaipaper.com/reportsuoluetu-zhiwang.png',
    desc: '选填上传官方<strong class="text-red">检测报告</strong>（PDF），辅助<strong class="text-red">独创学术模型</strong>精准优化'
  },
  大雅: {
    accept: '.pdf,application/pdf',
    preview: 'https://img.kuaipaper.com/reportsuoluetu-zhiwang.png',
    desc: '选填上传官方<strong class="text-red">检测报告</strong>（PDF），辅助<strong class="text-red">独创学术模型</strong>精准优化'
  },
  万方: {
    accept: '.pdf,application/pdf',
    preview: 'https://img.kuaipaper.com/reportsuoluetu-zhiwang.png',
    desc: '选填上传官方<strong class="text-red">检测报告</strong>（PDF），辅助<strong class="text-red">独创学术模型</strong>精准优化'
  }
}

function createRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'req-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10)
}

export default {
  name: 'WorkbenchMain',
  components: {
    TextDiffView
  },
  props: {
    readonly: {
      type: Boolean,
      default: false
    },
    balance: {
      type: Number,
      default: 0
    }
  },
  data() {
    const prefs = loadWorkbenchPrefs()
    return {
      MAX_CHARS_ZH,
      MAX_CHARS_EN,
      MIN_CHARS,
      activeService: prefs.activeService,
      activeLanguage: prefs.activeLanguage,
      activePlatform: prefs.activePlatform,
      inputMode: prefs.inputMode,
      content: '',
      uploadSession: null,
      mainUploading: false,
      reportUploading: false,
      mainDragOver: false,
      reportDragOver: false,
      processing: false,
      fileSubmitting: false,
      taskNo: '',
      resultContent: '',
      errorMessage: '',
      toastMessage: '',
      toastTimer: null,
      pendingDeleteRecord: null,
      deletingRecord: false,
      lastQuotaCost: 0,
      pollTimer: null,
      showDiff: false,
      records: [],
      recordsPage: 1,
      recordsHasMore: false,
      recordsLoading: false,
      docRecords: [],
      docRecordsPage: 1,
      docRecordsHasMore: false,
      docRecordsLoading: false,
      docRecordsPollTimer: null,
      recordDiffMode: {},
      services: [
        { key: 'DEDUP', label: '降重复率', iconSrc: jcSvg, desc: '降低查重重复率' },
        { key: 'AIGC', label: '降AIGC率', iconSrc: aigcSvg, desc: '降低 AI 检出率' },
        { key: 'DUAL', label: '双降', iconSrc: sjSvg, desc: '降重 + 降AIGC' },
        { key: 'POLISH', label: '学术润色', iconSrc: xsrsSvg, desc: '提升科研学术专业性' }
      ],
      languages: ['中文', '英文'],
      platformsZh: ['知网', '维普', '格子达', '大雅', '万方']
    }
  },
  computed: {
    platformPills() {
      return this.platformsZh
    },
    officialLink() {
      if (!this.needsPlatform || !this.activePlatform) {
        return null
      }
      return OFFICIAL_LINKS[this.activePlatform] || null
    },
    needsPlatform() {
      return this.activeService !== 'POLISH'
    },
    platformSelected() {
      if (!this.needsPlatform) {
        return true
      }
      return !!this.activePlatform
    },
    submitPlatform() {
      return this.needsPlatform ? this.activePlatform : ''
    },
    activeServiceLabel() {
      const item = this.services.find(s => s.key === this.activeService)
      return item ? item.label : ''
    },
    textMaxChars() {
      return this.activeLanguage === '英文' ? MAX_CHARS_EN : MAX_CHARS_ZH
    },
    charCount() {
      return this.content.length
    },
    estimatedCost() {
      return this.content.trim().length
    },
    balanceInsufficient() {
      if (this.readonly) {
        return false
      }
      return this.estimatedCost > this.balance
    },
    fileProcessingLabel() {
      if (this.fileSubmitting) {
        return '提交中...'
      }
      const platform = this.needsPlatform ? (this.activePlatform || '请选择平台') : this.activeLanguage
      if (this.activeService === 'AIGC') {
        return `一键全文降AI(${this.activeLanguage})(${platform})`
      }
      if (this.activeService === 'DEDUP') {
        return `一键全文降重(${this.activeLanguage})(${platform})`
      }
      if (this.activeService === 'POLISH') {
        return `一键全文润色(${this.activeLanguage})`
      }
      return `一键全文双降(${this.activeLanguage})(${platform})`
    },
    reportUploadEnabled() {
      return this.needsPlatform && !!this.uploadSession && !this.readonly && !this.mainUploading
    },
    dualSplitReportUpload() {
      return this.activeService === 'DUAL' && (this.activePlatform === '知网' || this.activePlatform === '维普')
    },
    aigcReportCardTitle() {
      return this.dualSplitReportUpload || this.activeService === 'AIGC' ? 'AIGC 检测报告' : '检测报告'
    },
    reportPlatformConfig() {
      return REPORT_PLATFORM_CONFIG[this.activePlatform] || REPORT_PLATFORM_CONFIG['知网']
    },
    reportPreviewImage() {
      return this.reportPlatformConfig.preview
    },
    reportAcceptTypes() {
      return this.reportPlatformConfig.accept
    },
    reportUploadDesc() {
      const cfg = this.reportPlatformConfig
      const platform = this.activePlatform
      if (platform === '知网' || platform === '维普') {
        if (this.activeService === 'DEDUP' && cfg.descDedup) {
          return cfg.descDedup
        }
        if (this.activeService === 'DUAL' && cfg.descDual) {
          return cfg.descDual
        }
        if (cfg.descAigc) {
          return cfg.descAigc
        }
      }
      return cfg.desc || ''
    },
    fileProcessingSubline() {
      const modelLine = {
        DEDUP: '由独创学术降重模型智能处理',
        AIGC: '由独创学术降AIGC模型智能处理',
        DUAL: '由独创学术模型协同智能处理',
        POLISH: '由独创学术润色模型智能处理'
      }
      const lead = modelLine[this.activeService] || '由独创学术模型智能处理'
      return `${lead} · 七天内段落可无限重生成`
    },
    reportFileIconClass() {
      if (this.activePlatform === '维普') {
        return 'bi bi-file-earmark-code'
      }
      return 'bi bi-file-earmark-pdf'
    },
    fileEstimatedCost() {
      if (!this.uploadSession) {
        return 0
      }
      return this.uploadSession.quotaCost || this.uploadSession.wordCount || 0
    },
    fileCostDetail() {
      const s = this.uploadSession
      if (!s) {
        return ''
      }
      const hasDedup = !!s.dedupReportFileName
      const hasAigc = !!s.reportFileName
      if (this.activeService === 'DUAL' && hasDedup && hasAigc) {
        return '检测报告已关联，独创学术模型协同优化'
      }
      if (this.activeService === 'DUAL' && (hasDedup || hasAigc)) {
        return '检测报告已关联，独创学术模型精准优化'
      }
      if (this.activeService === 'DEDUP' && hasDedup) {
        return '查重报告已关联，独创学术模型精准优化'
      }
      if (hasAigc && (this.activeService === 'AIGC' || this.activeService === 'DUAL')) {
        return 'AIGC 报告已关联，独创学术模型精准优化'
      }
      return ''
    },
    canSubmitFile() {
      if (this.readonly || this.processing || this.fileSubmitting || this.mainUploading || this.reportUploading) {
        return false
      }
      if (!this.uploadSession || !this.platformSelected) {
        return false
      }
      return this.isServiceEnabled(this.activeService)
    },
    canSubmit() {
      if (this.readonly || this.processing) {
        return false
      }
      if (this.inputMode !== 'text') {
        return false
      }
      const len = this.content.trim().length
      if (len < MIN_CHARS || len > this.textMaxChars) {
        return false
      }
      if (!this.isServiceEnabled(this.activeService)) {
        return false
      }
      if (!this.platformSelected) {
        return false
      }
      return !this.balanceInsufficient
    }
  },
  watch: {
    readonly(val) {
      if (!val) {
        this.loadHistoryRecords()
      } else {
        this.records = []
        this.docRecords = []
        this.stopDocRecordsPolling()
      }
    },
    activeService() {
      if (!this.readonly) {
        this.loadHistoryRecords()
      }
    },
    inputMode(mode) {
      if (this.readonly) {
        return
      }
      if (mode === 'file') {
        this.loadDocRecords(true)
        this.syncDocRecordsPolling()
      } else {
        this.stopDocRecordsPolling()
        this.loadRecords(true)
      }
    }
  },
  mounted() {
    this.applyRouteQuery()
    if (!this.readonly) {
      this.loadHistoryRecords()
    }
  },
  beforeDestroy() {
    this.stopPolling()
    this.stopDocRecordsPolling()
    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
    }
  },
  methods: {
    applyRouteQuery() {
      const service = this.$route.query.service
      const mode = this.$route.query.mode
      if (this.isServiceEnabled(service)) {
        this.activeService = service
      }
      if (mode === 'file') {
        this.inputMode = 'file'
      } else if (mode === 'text') {
        this.inputMode = 'text'
      }
    },
    guardAction() {
      if (this.readonly) {
        this.$emit('require-login')
        return false
      }
      return true
    },
    isServiceEnabled(serviceType) {
      return serviceType === 'AIGC' || serviceType === 'DEDUP' || serviceType === 'DUAL' || serviceType === 'POLISH'
    },
    switchService(key) {
      if (!this.readonly) {
        if (this.activeService !== key) {
          this.uploadSession = null
          this.resetEditorOnTabSwitch()
        }
        this.activeService = key
        this.errorMessage = ''
        this.persistWorkbenchPrefs()
      }
    },
    resetEditorOnTabSwitch() {
      this.stopPolling()
      this.content = ''
      this.resultContent = ''
      this.showDiff = false
      this.processing = false
      this.taskNo = ''
      this.lastQuotaCost = 0
    },
    switchLanguage(lang) {
      if (!this.readonly) {
        this.activeLanguage = lang
        if (lang === '英文' && !this.activePlatform) {
          this.activePlatform = 'Turnitin'
        }
        if (lang === '中文' && this.activePlatform === 'Turnitin') {
          this.activePlatform = ''
        }
        this.persistWorkbenchPrefs()
      }
    },
    switchPlatform(p) {
      if (!this.readonly) {
        this.activePlatform = p
        if (this.errorMessage === '请先选择检测平台') {
          this.errorMessage = ''
        }
        this.persistWorkbenchPrefs()
      }
    },
    scrollToPlatform() {
      const el = this.$el.querySelector('.platform-pills-wrap')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
    switchInputMode(mode) {
      if (!this.readonly) {
        this.inputMode = mode
        this.persistWorkbenchPrefs()
      }
    },
    persistWorkbenchPrefs() {
      saveWorkbenchPrefs({
        activeService: this.activeService,
        activeLanguage: this.activeLanguage,
        activePlatform: this.activePlatform,
        inputMode: this.inputMode
      })
    },
    handleBatchUpload() {
      this.errorMessage = '批量上传功能即将上线'
    },
    triggerMainFileInput() {
      if (!this.guardAction() || this.mainUploading || this.uploadSession) {
        return
      }
      if (!this.platformSelected) {
        if (this.needsPlatform) {
          this.errorMessage = '请先选择检测平台'
          this.scrollToPlatform()
        }
        return
      }
      this.$refs.mainFileInput && this.$refs.mainFileInput.click()
    },
    triggerReportFileInput() {
      if (!this.reportUploadEnabled || this.reportUploading) {
        return
      }
      this.$refs.reportFileInput && this.$refs.reportFileInput.click()
    },
    triggerDedupReportInput() {
      if (!this.reportUploadEnabled || this.reportUploading) {
        return
      }
      this.$refs.reportDedupInput && this.$refs.reportDedupInput.click()
    },
    triggerAigcReportInput() {
      if (!this.reportUploadEnabled || this.reportUploading) {
        return
      }
      this.$refs.reportAigcInput && this.$refs.reportAigcInput.click()
    },
    onMainFileChange(event) {
      const file = event.target.files && event.target.files[0]
      this.uploadMainFile(file)
      event.target.value = ''
    },
    onReportFileChange(event) {
      const file = event.target.files && event.target.files[0]
      this.uploadReportFile(file, 'auto')
      event.target.value = ''
    },
    onDedupReportFileChange(event) {
      const file = event.target.files && event.target.files[0]
      this.uploadReportFile(file, 'dedup')
      event.target.value = ''
    },
    onAigcReportFileChange(event) {
      const file = event.target.files && event.target.files[0]
      this.uploadReportFile(file, 'aigc')
      event.target.value = ''
    },
    onMainDragOver() {
      if (this.readonly || this.uploadSession || this.mainUploading) {
        return
      }
      this.mainDragOver = true
    },
    onMainDragLeave() {
      this.mainDragOver = false
    },
    onMainDrop(event) {
      this.mainDragOver = false
      if (!this.guardAction() || this.uploadSession || this.mainUploading) {
        return
      }
      if (!this.platformSelected) {
        if (this.needsPlatform) {
          this.errorMessage = '请先选择检测平台'
          this.scrollToPlatform()
        }
        return
      }
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]
      this.uploadMainFile(file)
    },
    onReportDragOver() {
      if (!this.reportUploadEnabled || this.reportUploading) {
        return
      }
      this.reportDragOver = true
    },
    onReportDragLeave() {
      this.reportDragOver = false
    },
    onReportDrop(event) {
      this.reportDragOver = false
      if (!this.reportUploadEnabled || this.reportUploading) {
        return
      }
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]
      this.uploadReportFile(file, 'auto')
    },
    uploadMainFile(file) {
      if (!file) {
        return
      }
      if (!this.isDocxFile(file)) {
        this.errorMessage = '仅支持 .docx 格式文件'
        return
      }
      this.errorMessage = ''
      this.mainUploading = true
      uploadProcessDocx(file, {
        serviceType: this.activeService,
        platform: this.submitPlatform,
        language: this.activeLanguage
      })
          .then(res => {
            this.uploadSession = res.data || null
          })
          .catch(err => {
            this.errorMessage = err.message || '文档上传失败'
          })
          .finally(() => {
            this.mainUploading = false
          })
    },
    uploadReportFile(file, reportRole = 'auto') {
      if (!file || !this.uploadSession) {
        return
      }
      if (!this.isReportFile(file)) {
        this.errorMessage = this.activePlatform === '维普' ? '维普检测报告仅支持 HTML 格式' : '检测报告仅支持 PDF 格式'
        return
      }
      this.errorMessage = ''
      this.reportUploading = true
      uploadProcessReport(this.uploadSession.uploadId, file, reportRole)
          .then(res => {
            this.uploadSession = res.data || this.uploadSession
          })
          .catch(err => {
            this.errorMessage = err.message || '报告上传失败'
          })
          .finally(() => {
            this.reportUploading = false
          })
    },
    clearMainFile() {
      if (!this.uploadSession) {
        return
      }
      const uploadId = this.uploadSession.uploadId
      this.mainUploading = true
      deleteProcessUpload(uploadId)
          .then(() => {
            this.uploadSession = null
          })
          .catch(err => {
            this.errorMessage = err.message || '删除文件失败'
          })
          .finally(() => {
            this.mainUploading = false
          })
    },
    clearDedupReportFile() {
      if (!this.uploadSession || !this.uploadSession.dedupReportFileName) {
        return
      }
      this.reportUploading = true
      deleteProcessReport(this.uploadSession.uploadId, 'dedup')
          .then(res => {
            this.uploadSession = res.data || this.uploadSession
          })
          .catch(err => {
            this.errorMessage = err.message || '删除查重报告失败'
          })
          .finally(() => {
            this.reportUploading = false
          })
    },
    clearAigcReportFile() {
      if (!this.uploadSession || !this.uploadSession.reportFileName) {
        return
      }
      this.reportUploading = true
      deleteProcessReport(this.uploadSession.uploadId, 'aigc')
          .then(res => {
            this.uploadSession = res.data || this.uploadSession
          })
          .catch(err => {
            this.errorMessage = err.message || '删除报告失败'
          })
          .finally(() => {
            this.reportUploading = false
          })
    },
    handleFileSubmit() {
      if (!this.guardAction()) {
        return
      }
      if (!this.canSubmitFile) {
        if (this.needsPlatform && !this.activePlatform) {
          this.errorMessage = '请先选择检测平台'
          this.scrollToPlatform()
        }
        return
      }
      this.errorMessage = ''
      this.fileSubmitting = true
      submitProcessFile({
        uploadId: this.uploadSession.uploadId,
        serviceType: this.activeService,
        platform: this.submitPlatform,
        language: this.activeLanguage,
        requestId: createRequestId()
      })
          .then(res => {
            const taskNo = res.data && res.data.taskNo
            if (!taskNo) {
              throw new Error('任务创建失败')
            }
            this.$emit('process-submitted')
            this.navigateToProcessTask(taskNo)
          })
          .catch(err => {
            this.errorMessage = err.message || '提交失败，请稍后重试'
          })
          .finally(() => {
            this.fileSubmitting = false
          })
    },
    isDocxFile(file) {
      const name = (file.name || '').toLowerCase()
      return name.endsWith('.docx') || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    },
    isReportFile(file) {
      const name = (file.name || '').toLowerCase()
      if (this.activePlatform === '维普') {
        return name.endsWith('.html') || name.endsWith('.htm') || file.type === 'text/html'
      }
      return name.endsWith('.pdf') || file.type === 'application/pdf'
    },
    scrollToRecords() {
      if (this.readonly) {
        this.guardAction()
        return
      }
      const targetId = this.inputMode === 'file' ? 'document-records' : 'task-records'
      const el = document.getElementById(targetId)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    clearContent() {
      if (this.guardAction()) {
        this.stopPolling()
        this.content = ''
        this.resultContent = ''
        this.errorMessage = ''
        this.processing = false
        this.taskNo = ''
        this.lastQuotaCost = 0
        this.showDiff = false
      }
    },
    handleSubmit() {
      if (!this.guardAction()) {
        return
      }
      if (!this.canSubmit) {
        if (this.needsPlatform && !this.activePlatform) {
          this.errorMessage = '请先选择检测平台'
          this.scrollToPlatform()
        } else if (this.balanceInsufficient) {
          this.errorMessage = '可用点数不足，请先充值'
          this.$emit('require-recharge')
        } else if (this.content.trim().length < MIN_CHARS) {
          this.errorMessage = '文本不得少于 ' + MIN_CHARS + ' 个字符'
        } else if (this.content.trim().length > this.textMaxChars) {
          this.errorMessage = '文本超过 ' + this.textMaxChars + ' 字符上限'
        }
        return
      }

      this.errorMessage = ''
      this.resultContent = ''
      this.showDiff = false
      this.processing = true
      this.lastQuotaCost = 0

      const requestId = createRequestId()
      submitProcess({
        serviceType: this.activeService,
        submitType: 'T',
        title: this.submitPlatform,
        language: this.activeLanguage,
        content: this.content.trim(),
        requestId
      })
          .then(res => {
            const data = res.data || {}
            this.taskNo = data.taskNo
            this.lastQuotaCost = data.quotaCost || this.estimatedCost
            this.$emit('process-submitted', data)
            this.startPolling()
          })
          .catch(err => {
            this.processing = false
            this.errorMessage = err.message || '提交失败'
          })
    },
    startPolling() {
      this.stopPolling()
      this.pollOnce()
      this.pollTimer = setInterval(() => {
        this.pollOnce()
      }, POLL_INTERVAL)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    pollOnce() {
      if (!this.taskNo) {
        return
      }
      getProcessTask(this.taskNo)
          .then(res => {
          const task = res.data || {}
          if (task.status === '2') {
              this.finishSuccess(task)
            } else if (task.status === '3') {
              this.finishFailed(task.errorMsg || '处理失败')
            }
          })
          .catch(err => {
            this.finishFailed(err.message || '查询任务失败')
          })
    },
    finishSuccess(task) {
      this.stopPolling()
      this.processing = false
      this.resultContent = task.outputContent || ''
      this.lastQuotaCost = task.quotaCost || this.lastQuotaCost
      this.$emit('process-success', task)
      this.loadRecords(true)
    },
    finishFailed(message) {
      this.stopPolling()
      this.processing = false
      this.errorMessage = message
      this.$emit('process-failed', { taskNo: this.taskNo, message })
    },
    loadHistoryRecords() {
      if (this.readonly) {
        return
      }
      if (this.inputMode === 'file') {
        this.loadDocRecords(true)
        this.syncDocRecordsPolling()
      } else {
        this.loadRecords(true)
      }
    },
    loadRecords(reset) {
      if (this.readonly) {
        return Promise.resolve()
      }
      if (reset) {
        this.recordsPage = 1
        this.recordsHasMore = false
      }
      this.recordsLoading = true
      return getProcessTasks({
        serviceType: this.activeService,
        submitType: 'T',
        pageNum: this.recordsPage,
        pageSize: PAGE_SIZE
      })
          .then(res => {
            const data = res.data || {}
            const list = (data.list || []).filter(item => !item.submitType || item.submitType === 'T')
            this.records = reset ? list : this.records.concat(list)
            this.recordsHasMore = !!data.hasMore
          })
          .catch(() => {
            if (reset) {
              this.records = []
            }
          })
          .finally(() => {
            this.recordsLoading = false
          })
    },
    loadMoreRecords() {
      if (!this.recordsHasMore || this.recordsLoading) {
        return
      }
      this.recordsPage += 1
      this.loadRecords(false)
    },
    loadDocRecords(reset) {
      if (this.readonly) {
        return Promise.resolve()
      }
      if (reset) {
        this.docRecordsPage = 1
        this.docRecordsHasMore = false
      }
      this.docRecordsLoading = true
      return getProcessTasks({
        serviceType: this.activeService,
        submitType: 'F',
        pageNum: this.docRecordsPage,
        pageSize: PAGE_SIZE,
        summary: true
      })
          .then(res => {
            if (this._isDestroyed || this.$route.name === 'process-task') {
              return
            }
            const data = res.data || {}
            const list = data.list || []
            this.docRecords = reset ? list : this.docRecords.concat(list)
            this.docRecordsHasMore = !!data.hasMore
            this.syncDocRecordsPolling()
          })
          .catch(() => {
            if (reset) {
              this.docRecords = []
            }
          })
          .finally(() => {
            this.docRecordsLoading = false
          })
    },
    loadMoreDocRecords() {
      if (!this.docRecordsHasMore || this.docRecordsLoading) {
        return
      }
      this.docRecordsPage += 1
      this.loadDocRecords(false)
    },
    docRecordStatusLine(item) {
      const progress = item.status === '2' ? 100 : (item.progress || 0)
      const labelMap = {
        '0': '进行中',
        '1': '进行中',
        '2': '已完成',
        '3': '处理失败',
        '4': '已暂停'
      }
      const label = labelMap[item.status] || '进行中'
      return `${label} | ${progress}%`
    },
    openDocRecord(item) {
      if (!item || !item.taskNo) {
        return
      }
      this.navigateToProcessTask(item.taskNo)
    },
    navigateToProcessTask(taskNo) {
      this.stopDocRecordsPolling()
      this.$router.push({ path: '/workbench/task', query: { taskId: taskNo } })
    },
    canDownloadDocRecord(item) {
      return !!(item && item.status === '2' && item.outputFileUrl)
    },
    downloadDocRecord(item) {
      if (this.canDownloadDocRecord(item)) {
        window.open(item.outputFileUrl, '_blank')
      }
    },
    deleteDocRecord(item) {
      if (!item || !item.taskNo) {
        return
      }
      this.pendingDeleteRecord = item
    },
    cancelDeleteRecord() {
      if (this.deletingRecord) {
        return
      }
      this.pendingDeleteRecord = null
    },
    confirmDeleteRecord() {
      const item = this.pendingDeleteRecord
      if (!item || !item.taskNo || this.deletingRecord) {
        return
      }
      this.deletingRecord = true
      deleteProcessRecord(item.taskNo)
          .then(() => {
            this.docRecords = this.docRecords.filter(row => row.taskNo !== item.taskNo)
            this.syncDocRecordsPolling()
            this.showToast('记录已删除')
          })
          .catch(err => {
            this.errorMessage = err.message || '删除失败'
          })
          .finally(() => {
            this.deletingRecord = false
            this.pendingDeleteRecord = null
          })
    },
    showToast(message) {
      if (this.toastTimer) {
        clearTimeout(this.toastTimer)
      }
      this.toastMessage = message
      this.toastTimer = setTimeout(() => {
        this.toastMessage = ''
        this.toastTimer = null
      }, 2000)
    },
    hasActiveDocRecords() {
      return this.docRecords.some(item => ['0', '1', '4'].includes(item.status))
    },
    startDocRecordsPolling() {
      if (this.readonly || this.inputMode !== 'file' || this.$route.name === 'process-task') {
        return
      }
      this.stopDocRecordsPolling()
      this.docRecordsPollTimer = setInterval(() => {
        if (this._isDestroyed || this.$route.name === 'process-task') {
          this.stopDocRecordsPolling()
          return
        }
        if (this.hasActiveDocRecords()) {
          this.loadDocRecords(true)
        } else {
          this.stopDocRecordsPolling()
        }
      }, DOC_RECORD_POLL_INTERVAL)
    },
    stopDocRecordsPolling() {
      if (this.docRecordsPollTimer) {
        clearInterval(this.docRecordsPollTimer)
        this.docRecordsPollTimer = null
      }
    },
    syncDocRecordsPolling() {
      if (this.$route.name === 'process-task') {
        this.stopDocRecordsPolling()
        return
      }
      if (this.inputMode === 'file' && this.hasActiveDocRecords()) {
        this.startDocRecordsPolling()
      } else {
        this.stopDocRecordsPolling()
      }
    },
    toggleRecordDiff(taskNo) {
      this.$set(this.recordDiffMode, taskNo, !this.recordDiffMode[taskNo])
    },
    recordTag(item) {
      return item.title || this.activePlatform || '文本'
    },
    formatRelativeTime(dateStr) {
      if (!dateStr) {
        return ''
      }
      const d = new Date(String(dateStr).replace(/-/g, '/'))
      if (Number.isNaN(d.getTime())) {
        return dateStr
      }
      const diff = Date.now() - d.getTime()
      const mins = Math.floor(diff / 60000)
      if (mins < 1) {
        return '不到 1 分钟前'
      }
      if (mins < 60) {
        return mins + ' 分钟前'
      }
      const hours = Math.floor(mins / 60)
      if (hours < 24) {
        return hours + ' 小时前'
      }
      const days = Math.floor(hours / 24)
      if (days < 30) {
        return days + ' 天前'
      }
      const months = Math.floor(days / 30)
      if (months < 12) {
        return '大约 ' + months + ' 个月前'
      }
      return '大约 ' + Math.floor(months / 12) + ' 年前'
    },
    copyText(text) {
      if (!text) {
        return
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.showToast('已复制到剪贴板')
        }).catch(() => {
          this.fallbackCopy(text)
        })
      } else {
        this.fallbackCopy(text)
      }
    },
    fallbackCopy(text) {
      const ta = document.createElement('textarea')
      ta.value = text
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      this.showToast('已复制到剪贴板')
    }
  }
}
</script>


<style scoped src="./WorkbenchMain.css"></style>
